import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import { sessionAPI, studentAPI } from '../services/api';

const EMPTY_FORM = {
  first_name: '',
  last_name: '',
  date_of_birth: '',
  session: '',
  is_new_student: false,
  school_district: '',
  parent_email: '',
  mother_name: '',
  mother_cell: '',
  father_name: '',
  father_cell: '',
};

function NewStudentForm({ open, onClose, onSuccess, defaultSessionId }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const loadSessions = useCallback(async () => {
    setSessionsLoading(true);
    try {
      const res = await sessionAPI.getSessions();
      const list = res.data.results || res.data || [];
      setSessions(Array.isArray(list) ? list : []);
    } catch (e) {
      console.error('Error loading sessions:', e);
      setSessions([]);
    } finally {
      setSessionsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    setForm({
      ...EMPTY_FORM,
      session: defaultSessionId ? String(defaultSessionId) : '',
    });
    setError('');
    loadSessions();
  }, [open, defaultSessionId, loadSessions]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const firstName = (form.first_name || '').trim();
    const lastName = (form.last_name || '').trim();
    const dob = (form.date_of_birth || '').trim();
    const sessionId = form.session ? Number(form.session) : null;

    if (!firstName || !lastName || !dob || !sessionId) {
      setError('First name, last name, date of birth, and session are required.');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        first_name: firstName,
        last_name: lastName,
        date_of_birth: dob,
        session: sessionId,
        is_new_student: Boolean(form.is_new_student),
        school_district: (form.school_district || '').trim() || null,
        parent_email: (form.parent_email || '').trim() || null,
        mother_cell: (form.mother_cell || '').trim() || null,
        father_cell: (form.father_cell || '').trim() || null,
      };

      const res = await studentAPI.createStudent(payload);
      const studentId = res.data?.id;

      const pgPatch = {};
      const motherName = (form.mother_name || '').trim();
      const fatherName = (form.father_name || '').trim();
      if (motherName) pgPatch.mother_name = motherName;
      if (fatherName) pgPatch.father_name = fatherName;

      if (studentId && Object.keys(pgPatch).length > 0) {
        await studentAPI.updateParentGuardian(studentId, pgPatch);
      }

      onSuccess?.(res.data);
      onClose?.();
    } catch (err) {
      const detail = err.response?.data
        ? (typeof err.response.data === 'string'
          ? err.response.data
          : JSON.stringify(err.response.data))
        : err.message;
      setError(detail || 'Failed to create student.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Student</DialogTitle>
      <Box component="form" onSubmit={handleSubmit}>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {error && (
            <Alert severity="error" onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          <TextField
            label="First Name"
            value={form.first_name}
            onChange={(e) => handleChange('first_name', e.target.value)}
            required
            fullWidth
            autoFocus
          />
          <TextField
            label="Last Name"
            value={form.last_name}
            onChange={(e) => handleChange('last_name', e.target.value)}
            required
            fullWidth
          />
          <TextField
            label="Date of Birth"
            type="date"
            value={form.date_of_birth}
            onChange={(e) => handleChange('date_of_birth', e.target.value)}
            required
            fullWidth
            InputLabelProps={{ shrink: true }}
          />

          <FormControl fullWidth required size="small">
            <InputLabel>Session</InputLabel>
            <Select
              label="Session"
              value={form.session}
              onChange={(e) => handleChange('session', e.target.value)}
              disabled={sessionsLoading}
            >
              {sessions.map((s) => (
                <MenuItem key={s.id} value={String(s.id)}>
                  {s.display_name || s.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControlLabel
            control={
              <Checkbox
                checked={form.is_new_student}
                onChange={(e) => handleChange('is_new_student', e.target.checked)}
              />
            }
            label="New Student"
          />

          <TextField
            label="School District"
            value={form.school_district}
            onChange={(e) => handleChange('school_district', e.target.value)}
            placeholder='e.g. "Nassau County" or "NYC DOE"'
            fullWidth
          />
          <TextField
            label="Parent Email"
            type="email"
            value={form.parent_email}
            onChange={(e) => handleChange('parent_email', e.target.value)}
            fullWidth
          />

          <TextField
            label="Mother Name"
            value={form.mother_name}
            onChange={(e) => handleChange('mother_name', e.target.value)}
            fullWidth
          />
          <TextField
            label="Mother Cell"
            value={form.mother_cell}
            onChange={(e) => handleChange('mother_cell', e.target.value)}
            fullWidth
          />
          <TextField
            label="Father Name"
            value={form.father_name}
            onChange={(e) => handleChange('father_name', e.target.value)}
            fullWidth
          />
          <TextField
            label="Father Cell"
            value={form.father_cell}
            onChange={(e) => handleChange('father_cell', e.target.value)}
            fullWidth
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={submitting || sessionsLoading}>
            {submitting ? <CircularProgress size={22} /> : 'Create Student'}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}

export default NewStudentForm;
